import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  type FC,
  type PropsWithChildren,
  type RefObject,
} from 'react';

// Import fabric for side effects (loads the library)
// The types come from @types/fabric which declares fabric as a global namespace
import 'fabric';
import { useHistoryStore } from '../stores/historyStore';

interface CanvasContextValue {
  canvas: fabric.Canvas | null;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  isReady: boolean;
  initCanvas: (element: HTMLCanvasElement) => void;
  disposeCanvas: () => void;
}

const CanvasContext = createContext<CanvasContextValue | null>(null);

export const CanvasProvider: FC<PropsWithChildren> = ({ children }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [isReady, setIsReady] = useState(false);

  const initCanvas = (element: HTMLCanvasElement) => {
    // Dispose existing canvas if any
    if (canvas) {
      canvas.dispose();
    }

    // Access fabric from window since it's a UMD library
    const f = window.fabric;

    // Create new Fabric canvas
    const fabricCanvas = new f.Canvas(element, {
      width: 800,
      height: 800,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
      controlsAboveOverlay: true,
    });

    // Set initial zoom to 1.0 (no zoom)
    fabricCanvas.setZoom(1.0);

    // Set default object styles
    f.Object.prototype.set({
      transparentCorners: false,
      cornerColor: '#8BC34A',
      cornerStrokeColor: '#8BC34A',
      borderColor: '#8BC34A',
      cornerSize: 10,
      padding: 5,
      cornerStyle: 'circle',
    });

    // Add custom delete control to all objects
    const deleteControl = new f.Control({
      x: 0.5,
      y: -0.5,
      offsetY: -16,
      offsetX: 16,
      cursorStyle: 'pointer',
      mouseUpHandler: (_eventData, transformData) => {
        const target = transformData.target;
        if (target && !target.isGuide && !target.isMockup && !target.isBaseImage && !target.isMaskOverlay) {
          fabricCanvas.discardActiveObject();
          fabricCanvas.remove(target);
          fabricCanvas.renderAll();

          // Save to history so deletion can be undone
          const state = JSON.stringify(
            fabricCanvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay', 'uploadedUrl', 'serverUrl', 'laserConverted', 'convertedFrom'])
          );
          useHistoryStore.getState().saveSnapshot(state);
        }
        return true;
      },
      render: (ctx, left, top, _styleOverride, _fabricObject) => {
        const size = 24;
        ctx.save();
        ctx.translate(left, top);

        // Draw circle background
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fillStyle = '#ef4444';
        ctx.fill();

        // Draw X icon
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        const iconSize = 6;
        ctx.beginPath();
        ctx.moveTo(-iconSize, -iconSize);
        ctx.lineTo(iconSize, iconSize);
        ctx.moveTo(iconSize, -iconSize);
        ctx.lineTo(-iconSize, iconSize);
        ctx.stroke();

        ctx.restore();
      },
    });

    // Add to default controls on Object prototype
    f.Object.prototype.controls.deleteControl = deleteControl;

    setCanvas(fabricCanvas);
    setIsReady(true);
  };

  const disposeCanvas = () => {
    if (canvas) {
      canvas.dispose();
      setCanvas(null);
      setIsReady(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (canvas) {
        canvas.dispose();
      }
    };
  }, [canvas]);

  return (
    <CanvasContext.Provider
      value={{
        canvas,
        canvasRef,
        isReady,
        initCanvas,
        disposeCanvas,
      }}
    >
      {children}
    </CanvasContext.Provider>
  );
};

export const useCanvas = (): CanvasContextValue => {
  const context = useContext(CanvasContext);
  if (!context) {
    throw new Error('useCanvas must be used within a CanvasProvider');
  }
  return context;
};

export default CanvasContext;
